<script setup lang="ts">
import { ArrowUpTrayIcon } from '@heroicons/vue/24/solid'
import { ref, watch } from 'vue'

import type { ImageObject } from '@/types/external/2work/doctypes/CustomDoctypes'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { translationFunctionDefault } from '@/utilities/translations'

interface ImageUploadProps {
  labelText?: string | undefined
}

defineProps<ImageUploadProps>()
defineSlots<{
  default: unknown
}>()

const tfDefault = translationFunctionDefault()

const simpleNotificationStore = useSimpleNotificationStore()

const modelValue = defineModel<Partial<ImageObject> | undefined>()

const inputRef = ref<HTMLInputElement>()
const valueRef = ref<Partial<ImageObject>>()

const fileReader = new FileReader()

fileReader.onloadend = (event): void => {
  const currentResult = event.target?.result
  if (typeof currentResult === 'string') {
    valueRef.value = {
      ...valueRef.value,
      content: currentResult,
    }
  } else {
    console.error('Component: "ImageUpload" - Error: got not expected file result')
  }
}

const handleFileChange = (): void => {
  const allowedFileTypes = ['image/png', 'image/jpeg', 'image/gif']
  const files = inputRef.value?.files

  if (files && files.length > 0) {
    const [file] = files

    if (file && allowedFileTypes.includes(file.type)) {
      valueRef.value = {
        ...valueRef.value,
        filename: file.name,
      }

      fileReader.readAsDataURL(file)
    } else {
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('imageUpload.noFileSupportError'),
      )
    }
  }
}

watch(valueRef, (val) => {
  modelValue.value = val
})
</script>

<template>
  <div class="flex items-center justify-center" :class="[labelText ? 'gap-5' : 'gap-0']">
    <div class="relative">
      <slot></slot>

      <label
        class="absolute bottom-0 right-0 block h-8 w-8 cursor-pointer rounded-full border-2 border-white bg-lochmara-600"
        for="fileinput"
      >
        <div class="grid h-full w-full place-content-center">
          <ArrowUpTrayIcon class="m-auto h-5 w-5 text-white" />
          <input
            id="fileinput"
            ref="inputRef"
            accept="image/*"
            class="sr-only"
            :data-testid="TEST_IDS_CREATE.sectionImageUpload.uploadInput"
            type="file"
            @change="handleFileChange"
          />
        </div>
      </label>
    </div>
    <label v-if="labelText" class="cursor-pointer text-sm text-black-900" for="fileinput">
      {{ labelText }}
    </label>
  </div>
</template>
